import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import { adminAuth, adminApi } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  ArrowRight,
  PillBottle,
  FileText,
  Save,
  Trash2,
} from "lucide-react";

const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return e?.message || "Something went wrong";
};

// Blank form state — arrays start with one empty field each so the user
// always sees at least one input on both languages.
// Stable-ID helpers. We store each name as { id, value } so React can key
// on `id` (never on array index) — this keeps input focus and IME state
// intact when a middle row is removed.
let _rowSeq = 0;
const _newId = () => `row-${++_rowSeq}-${Date.now().toString(36)}`;
const _wrap = (arr) => (Array.isArray(arr) && arr.length ? arr : [""]).map((v) => ({ id: _newId(), value: v || "" }));

const EMPTY_FORM = {
  names_ar: _wrap([""]),
  names_en: _wrap([""]),
  serial_code: "",
  reason: "",
  doctor_note: "يجب مراجعة الطبيب لاستخدام هذا الدواء تحت الإشراف الطبي.",
};

export default function AdminAdd() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    adminAuth
      .me()
      .then(() => setAuthed(true))
      .catch(() => navigate("/admin"))
      .finally(() => setChecking(false));
  }, [navigate]);

  useEffect(() => {
    if (!authed || !isEdit) return;
    setLoadingRecord(true);
    adminApi
      .get(editId)
      .then((doc) => {
        // Prefer structured arrays; fall back to legacy single string.
        const ar = Array.isArray(doc.names_ar) && doc.names_ar.length ? doc.names_ar : (doc.name ? [doc.name] : [""]);
        const en = Array.isArray(doc.names_en) && doc.names_en.length ? doc.names_en : (doc.name_en ? [doc.name_en] : [""]);
        setForm({
          names_ar: _wrap(ar),
          names_en: _wrap(en.length ? en : [""]),
          serial_code: doc.serial_code || "",
          reason: doc.reason || "",
          doctor_note: doc.doctor_note || "",
        });
      })
      .catch((e) => {
        if (e?.response?.status === 401) navigate("/admin");
        else toast.error("تعذّر تحميل بيانات الدواء");
      })
      .finally(() => setLoadingRecord(false));
  }, [authed, isEdit, editId, navigate]);

  const updateNameAt = (lang, idx, value) => {
    setForm((prev) => {
      const list = prev[lang].map((row, i) => (i === idx ? { ...row, value } : row));
      return { ...prev, [lang]: list };
    });
  };
  const addNameField = (lang) => {
    setForm((prev) => ({ ...prev, [lang]: [...prev[lang], { id: _newId(), value: "" }] }));
  };
  const removeNameAt = (lang, idx) => {
    setForm((prev) => {
      const list = prev[lang].filter((_, i) => i !== idx);
      // Never leave the list empty — always keep one input visible.
      return { ...prev, [lang]: list.length ? list : [{ id: _newId(), value: "" }] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Trim + drop empties + dedupe client-side; server enforces the same rules.
    const cleanAr = Array.from(new Set(form.names_ar.map((r) => (r.value || "").trim()).filter(Boolean)));
    const cleanEn = Array.from(
      new Map(
        form.names_en
          .map((r) => (r.value || "").trim())
          .filter(Boolean)
          .map((v) => [v.toLowerCase(), v])
      ).values()
    );
    if (cleanAr.length === 0) {
      toast.error("يجب إدخال اسم عربي واحد على الأقل");
      return;
    }
    if (!form.reason.trim()) {
      toast.error("سبب التقييد مطلوب");
      return;
    }
    const primaryName = cleanAr[0];
    const payload = {
      names_ar: cleanAr,
      names_en: cleanEn,
      // Server derives name/name_en canonically from the arrays; sending them
      // here is optional but keeps the wire shape stable for existing clients.
      name: primaryName,
      name_en: cleanEn[0] || "",
      serial_code: form.serial_code || "",
      reason: form.reason,
      doctor_note: form.doctor_note,
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await adminApi.update(editId, payload);
        toast.success(`تم تحديث "${primaryName}" بنجاح`);
        navigate("/admin");
      } else {
        await adminApi.add(payload);
        toast.success(`تمت إضافة "${primaryName}" لقائمة التقييد بنجاح`);
        // Reset with fresh row IDs so key stays unique across cycles.
        setForm({
          names_ar: _wrap([""]),
          names_en: _wrap([""]),
          serial_code: "",
          reason: "",
          doctor_note: "يجب مراجعة الطبيب لاستخدام هذا الدواء تحت الإشراف الطبي.",
        });
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin");
      } else {
        toast.error(formatErr(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }
  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-10 border-b-2 border-slate-900 pb-6">
          <div>
            <div className="text-sm font-bold tracking-tight text-slate-500 mb-2">
              {isEdit ? "دوائي / تعديل دواء" : "دوائي / إضافة دواء"}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">
              {isEdit ? "تعديل بيانات الدواء" : "إضافة دواء مقيد جديد"}
            </h1>
            <p className="text-slate-600 mt-3 max-w-xl leading-relaxed">
              {isEdit
                ? "قم بتعديل الأسماء والحقول ثم احفظ التغييرات لتظهر مباشرة في الصفحة الرئيسية."
                : "أدخل اسم الدواء (يمكنك إضافة أكثر من اسم بديل) ليظهر مباشرة في نتائج البحث."}
            </p>
          </div>
          <button
            data-testid="back-to-admin-btn"
            onClick={() => navigate("/admin")}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-xl transition-colors cursor-pointer"
          >
            <span>عرض القائمة</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        <form
          data-testid="admin-add-form"
          onSubmit={handleSubmit}
          className={`bg-white border-2 border-slate-200 rounded-2xl p-6 sm:p-8 space-y-6 ${loadingRecord ? "opacity-50 pointer-events-none" : ""}`}
        >
          <NameList
            icon={<PillBottle className="w-4 h-4" />}
            label="الأسماء العربية للدواء"
            required
            testidPrefix="add-input-name-ar"
            addTestid="add-name-ar-btn"
            addLabel="+ إضافة اسم عربي"
            placeholderFor={(i) => (i === 0 ? "مثال: ترامادول" : "اسم بديل بالعربية")}
            values={form.names_ar}
            onChange={(i, v) => updateNameAt("names_ar", i, v)}
            onAdd={() => addNameField("names_ar")}
            onRemove={(i) => removeNameAt("names_ar", i)}
            dir="rtl"
          />

          <NameList
            icon={<PillBottle className="w-4 h-4" />}
            label="الأسماء الإنجليزية للدواء"
            testidPrefix="add-input-name-en"
            addTestid="add-name-en-btn"
            addLabel="+ Add English Name"
            placeholderFor={(i) => (i === 0 ? "Tramadol" : "Alternate English name")}
            values={form.names_en}
            onChange={(i, v) => updateNameAt("names_en", i, v)}
            onAdd={() => addNameField("names_en")}
            onRemove={(i) => removeNameAt("names_en", i)}
            dir="ltr"
          />

          <FieldGroup
            icon={<FileText className="w-4 h-4" />}
            label="وصف الدواء / سبب التقييد"
            required
          >
            <textarea
              data-testid="add-input-reason"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="اكتب وصف الدواء وسبب إدراجه ضمن الأدوية المقيدة..."
              rows={4}
              className="w-full bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-3 text-slate-900 font-semibold text-base leading-relaxed resize-none"
            />
          </FieldGroup>

          <FieldGroup
            icon={<FileText className="w-4 h-4" />}
            label="الإرشادات الطبية"
          >
            <textarea
              data-testid="add-input-doctor-note"
              value={form.doctor_note}
              onChange={(e) => setForm({ ...form, doctor_note: e.target.value })}
              rows={3}
              className="w-full bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-3 text-slate-900 font-semibold text-base leading-relaxed resize-none"
            />
          </FieldGroup>

          <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              data-testid="admin-add-submit-btn"
              type="submit"
              disabled={submitting}
              className="flex-1 bg-slate-900 text-white font-bold py-4 rounded-xl hover:bg-slate-800 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2 text-lg"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جارٍ الحفظ</span>
                </>
              ) : isEdit ? (
                <>
                  <Save className="w-5 h-5" />
                  <span>حفظ التعديلات</span>
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  <span>إضافة الدواء</span>
                </>
              )}
            </button>
            <button
              data-testid="admin-add-cancel-btn"
              type="button"
              onClick={() => navigate("/admin")}
              className="px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold rounded-xl transition-colors cursor-pointer"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const FieldGroup = ({ icon, label, required, children }) => (
  <div>
    <label className="flex items-center gap-2 text-sm font-bold tracking-tight text-slate-500 mb-2">
      <span className="text-slate-700">{icon}</span>
      <span>{label}</span>
      {required && <span className="text-red-600">*</span>}
    </label>
    {children}
  </div>
);

const NameList = ({
  icon,
  label,
  required,
  values,
  onChange,
  onAdd,
  onRemove,
  addLabel,
  addTestid,
  testidPrefix,
  placeholderFor,
  dir,
}) => (
  <div>
    <label className="flex items-center gap-2 text-sm font-bold tracking-tight text-slate-500 mb-2">
      <span className="text-slate-700">{icon}</span>
      <span>{label}</span>
      {required && <span className="text-red-600">*</span>}
    </label>
    <div className="space-y-2">
      {values.map((row, i) => {
        const isLast = i === values.length - 1;
        return (
        <div key={row.id} className="flex items-center gap-2">
          <input
            data-testid={`${testidPrefix}-${i}`}
            type="text"
            value={row.value}
            onChange={(e) => onChange(i, e.target.value)}
            onKeyDown={(e) => {
              // Only forward-Tab (not Shift+Tab) on the LAST input auto-creates
              // a new empty row and moves focus to it. Skip if the current
              // input is empty (avoid stacking empty rows). Normal Tab
              // behavior applies to any other field.
              if (
                e.key === "Tab" &&
                !e.shiftKey &&
                isLast &&
                (row.value || "").trim().length > 0
              ) {
                e.preventDefault();
                onAdd();
                // The newly added row lives at index `values.length`; focus
                // it once React has rendered the additional input.
                const nextIdx = values.length;
                requestAnimationFrame(() => {
                  const el = document.querySelector(
                    `[data-testid="${testidPrefix}-${nextIdx}"]`
                  );
                  if (el) el.focus();
                });
              }
            }}
            placeholder={placeholderFor(i)}
            dir={dir}
            className="flex-1 bg-slate-50 border-2 border-slate-200 focus:outline-none focus:border-slate-900 rounded-xl px-4 py-3 text-slate-900 font-bold text-base"
          />
          {values.length > 1 && (
            <button
              type="button"
              data-testid={`${testidPrefix}-remove-${i}`}
              onClick={() => onRemove(i)}
              className="w-10 h-10 shrink-0 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 border-2 border-red-200 flex items-center justify-center transition-colors cursor-pointer"
              title="حذف هذا الاسم"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        );
      })}
      <button
        type="button"
        data-testid={addTestid}
        onClick={onAdd}
        className="inline-flex items-center gap-2 text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-2 rounded-xl transition-colors cursor-pointer"
      >
        <Plus className="w-4 h-4" />
        <span>{addLabel}</span>
      </button>
    </div>
  </div>
);
